// Zvukový engine nad Web Audio. Hraje nahrané zvuky z /sounds/ (podle manifestu),
// a když soubor chybí, použije syntetizovanou náhradu. Panoráma přes StereoPannerNode.

export type SynthFn = (ctx: AudioContext, out: AudioNode, t: number) => number;

interface PlayOptions {
  pan?: number;
  gain?: number;
  /** Zpoždění v sekundách od teď. */
  delay?: number;
}

const synths = new Map<string, SynthFn>();
const buffers = new Map<string, AudioBuffer[]>();
let manifest: Record<string, string[]> = {};
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let volume = 0.8;
let queueEnd = 0;
let muted = false;

export function registerSynth(name: string, fn: SynthFn) {
  synths.set(name, fn);
}

export function setVolume(v: number) {
  volume = v;
  if (master && ctx) master.gain.setTargetAtTime(v, ctx.currentTime, 0.02);
}

/** V režimu „obraz“ je zvuk vypnutý. */
export function setMuted(m: boolean) {
  muted = m;
}

export function audioState(): string {
  return ctx?.state ?? "none";
}

/** Musí se zavolat z obsluhy klepnutí/klávesy (tlačítko „Hrát“). Odemyká zvuk na iOS. */
export function unlockAudio() {
  if (!ctx) {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = volume;
    master.connect(ctx.destination);
    installResumeHooks();
    void loadManifest();
  }
  resume();
  // Tichý buffer: Safari po něm zvuk spolehlivě odemkne.
  const b = ctx.createBuffer(1, 1, 22050);
  const s = ctx.createBufferSource();
  s.buffer = b;
  s.connect(ctx.destination);
  s.start(0);
}

function resume() {
  if (ctx && ctx.state !== "running" && ctx.state !== "closed") void ctx.resume().catch(() => {});
}

function installResumeHooks() {
  // Po zamčení iPhonu je kontext „interrupted“/„suspended“; probudíme ho při návratu nebo dalším dotyku.
  document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && resume());
  window.addEventListener("pageshow", resume);
  window.addEventListener("focus", resume);
  for (const ev of ["pointerdown", "touchend", "keydown"]) document.addEventListener(ev, resume, { capture: true, passive: true });
}

async function loadManifest() {
  try {
    const res = await fetch("/sounds/manifest.json", { cache: "no-cache" });
    if (res.ok) manifest = (await res.json()).sounds ?? {};
  } catch {
    manifest = {};
  }
  // Načteme vše na pozadí; dokud není hotovo, hraje syntéza.
  for (const [name, files] of Object.entries(manifest)) void loadSound(name, files);
}

async function loadSound(name: string, files: string[]) {
  const list: AudioBuffer[] = [];
  for (const f of files) {
    try {
      const res = await fetch(`/sounds/${f}`);
      if (!res.ok) continue;
      const data = await res.arrayBuffer();
      list.push(await ctx!.decodeAudioData(data));
    } catch {
      /* zůstane syntéza */
    }
  }
  if (list.length) buffers.set(name, list);
}

/** Přehraje zvuk hned (nebo s delay). Vrací délku v sekundách. */
export function play(name: string, opts: PlayOptions = {}): number {
  if (!ctx || !master || muted) return 0;
  const t = ctx.currentTime + (opts.delay ?? 0);
  return playAt(name, t, opts);
}

/** Zařadí zvuk za předchozí zařazené zvuky (aby se karta a „shoda“ nepřekrývaly). */
export function queue(name: string, opts: PlayOptions = {}, gap = 0.05): number {
  if (!ctx || !master || muted) return 0;
  const t = Math.max(ctx.currentTime + (opts.delay ?? 0), queueEnd);
  const d = playAt(name, t, opts);
  queueEnd = t + d + gap;
  return d;
}

function playAt(name: string, t: number, opts: PlayOptions): number {
  const c = ctx!;
  const gain = c.createGain();
  gain.gain.value = opts.gain ?? 1;
  let out: AudioNode = gain;
  if (opts.pan !== undefined && typeof c.createStereoPanner === "function") {
    const p = c.createStereoPanner();
    p.pan.value = Math.max(-1, Math.min(1, opts.pan));
    p.connect(gain);
    out = p;
  }
  gain.connect(master!);

  const list = buffers.get(name);
  if (list?.length) {
    const src = c.createBufferSource();
    src.buffer = list[Math.floor(Math.random() * list.length)];
    src.connect(out);
    src.start(t);
    return src.buffer.duration;
  }
  const synth = synths.get(name);
  return synth ? synth(c, out, t) : 0;
}

// --- Pomůcky pro syntézu ---

export function tone(
  c: AudioContext,
  out: AudioNode,
  t: number,
  freq: number,
  dur: number,
  opts: { type?: OscillatorType; gain?: number; to?: number; attack?: number } = {},
) {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = opts.type ?? "sine";
  o.frequency.setValueAtTime(freq, t);
  if (opts.to) o.frequency.exponentialRampToValueAtTime(opts.to, t + dur);
  const peak = opts.gain ?? 0.3;
  const a = opts.attack ?? 0.01;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + a);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g).connect(out);
  o.start(t);
  o.stop(t + dur + 0.02);
}

export function noise(c: AudioContext, out: AudioNode, t: number, dur: number, opts: { gain?: number; freq?: number; q?: number } = {}) {
  const len = Math.ceil(c.sampleRate * dur);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = "bandpass";
  f.frequency.value = opts.freq ?? 2000;
  f.Q.value = opts.q ?? 1;
  const g = c.createGain();
  g.gain.setValueAtTime(opts.gain ?? 0.3, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(f).connect(g).connect(out);
  src.start(t);
}

// --- Obecné UI zvuky (náhrady) ---

registerSynth("otoceni", (c, o, t) => {
  noise(c, o, t, 0.08, { freq: 3000, q: 0.8, gain: 0.25 });
  return 0.1;
});
registerSynth("shoda", (c, o, t) => {
  [523, 659, 784, 1047].forEach((f, i) => tone(c, o, t + i * 0.07, f, 0.25, { type: "triangle", gain: 0.25 }));
  return 0.5;
});
registerSynth("neshoda", (c, o, t) => {
  tone(c, o, t, 330, 0.18, { type: "square", gain: 0.12 });
  tone(c, o, t + 0.18, 247, 0.3, { type: "square", gain: 0.12 });
  return 0.5;
});
registerSynth("tvuj-tah", (c, o, t) => {
  tone(c, o, t, 880, 0.12, { gain: 0.3 });
  tone(c, o, t + 0.12, 1320, 0.2, { gain: 0.3 });
  return 0.35;
});
registerSynth("souper-tah", (c, o, t) => {
  tone(c, o, t, 440, 0.15, { type: "triangle", gain: 0.2 });
  return 0.2;
});
registerSynth("vyhra", (c, o, t) => {
  [523, 659, 784, 659, 784, 1047].forEach((f, i) => tone(c, o, t + i * 0.12, f, 0.3, { type: "triangle", gain: 0.25 }));
  return 1.0;
});
registerSynth("prohra", (c, o, t) => {
  [392, 370, 349, 330].forEach((f, i) => tone(c, o, t + i * 0.2, f, 0.35, { type: "sawtooth", gain: 0.08 }));
  return 1.0;
});
registerSynth("pripojeni", (c, o, t) => {
  tone(c, o, t, 600, 0.3, { to: 1200, gain: 0.2 });
  return 0.3;
});
registerSynth("odpojeni", (c, o, t) => {
  tone(c, o, t, 1200, 0.3, { to: 500, gain: 0.2 });
  return 0.3;
});
registerSynth("kurzor", (c, o, t) => {
  tone(c, o, t, 1500, 0.03, { type: "sine", gain: 0.15, attack: 0.002 });
  return 0.04;
});
registerSynth("okraj", (c, o, t) => {
  tone(c, o, t, 120, 0.1, { type: "square", gain: 0.15, attack: 0.002 });
  return 0.1;
});
